import { useState, useRef, useCallback, useEffect } from "react";

const STORAGE_KEY = "bot_engine_state";
const MAX_LOGS = 50;

export function useBotEngine() {
  // Carregar estado salvo
  const loadSaved = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  };

  const saved = loadSaved();

  // Restaurar status salvo: se estava running → vai reiniciar; se paused → mantém paused
  const savedStatus = saved.status ?? "idle";
  const initialStatus = (savedStatus === "running" || savedStatus === "paused") ? savedStatus : "idle";

  const [status, setStatus] = useState(initialStatus);
  const [startId, setStartId] = useState(saved.startId ?? 10000000);
  const [concurrency, setConcurrency] = useState(saved.concurrency ?? 3);
  const [currentId, setCurrentId] = useState(saved.currentId ?? saved.startId ?? 10000000);
  const [successCount, setSuccessCount] = useState(saved.successCount ?? 0);
  const [errorCount, setErrorCount] = useState(saved.errorCount ?? 0);
  const [logs, setLogs] = useState([]);
  const [reqPerMin, setReqPerMin] = useState(0);

  // Refs para controle sem re-render
  const isRunningRef = useRef(false);
  const isPausedRef = useRef(initialStatus === "paused");
  const currentIdRef = useRef(saved.currentId ?? saved.startId ?? 10000000);
  const activeCountRef = useRef(0);
  const concurrencyRef = useRef(saved.concurrency ?? 3);
  const successRef = useRef(saved.successCount ?? 0);
  const errorRef = useRef(saved.errorCount ?? 0);
  const reqTimestampsRef = useRef([]);
  const launchNextRef = useRef(null);
  const autoStartedRef = useRef(false);

  // Persist state (inclui status para restaurar no reload)
  const persistState = useCallback((overrideStatus) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        startId,
        currentId: currentIdRef.current,
        concurrency: concurrencyRef.current,
        successCount: successRef.current,
        errorCount: errorRef.current,
        status: overrideStatus !== undefined ? overrideStatus : (isRunningRef.current ? (isPausedRef.current ? "paused" : "running") : "idle"),
      }));
    } catch {}
  }, [startId]);

  // Calcular req/min
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      reqTimestampsRef.current = reqTimestampsRef.current.filter(t => now - t < 60000);
      setReqPerMin(reqTimestampsRef.current.length);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Adicionar log
  const addLog = useCallback((entry) => {
    setLogs(prev => [entry, ...prev].slice(0, MAX_LOGS));
  }, []);

  // Fazer requisição para um user_id
  const fetchUserId = useCallback(async (userId) => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const apiToken = process.env.REACT_APP_API_TOKEN;
    try {
      const response = await fetch(`${apiUrl}/api/rotabotv1`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Token": apiToken,
        },
        body: JSON.stringify({ user_id: String(userId) }),
        signal: AbortSignal.timeout(45000),
      });

      reqTimestampsRef.current.push(Date.now());

      if (response.ok) {
        const data = await response.json();

        // Já existe no banco — pular silenciosamente (sem contar como sucesso ou erro)
        if (data?.skipped === true || data?.status === "already_exists") {
          addLog({
            type: "skip",
            userId,
            msg: "já existe",
            time: new Date().toLocaleTimeString("pt-BR"),
          });
          return;
        }

        const username = data?.username || data?.profile?.username || "unknown";
        successRef.current += 1;
        setSuccessCount(v => v + 1);
        addLog({
          type: "success",
          userId,
          username,
          time: new Date().toLocaleTimeString("pt-BR"),
        });
      } else if (response.status === 400) {
        // Usuário não encontrado - normal, continua
        errorRef.current += 1;
        setErrorCount(v => v + 1);
        addLog({
          type: "skip",
          userId,
          msg: `ID inválido`,
          time: new Date().toLocaleTimeString("pt-BR"),
        });
      } else {
        errorRef.current += 1;
        setErrorCount(v => v + 1);
        addLog({
          type: "error",
          userId,
          msg: `HTTP ${response.status}`,
          time: new Date().toLocaleTimeString("pt-BR"),
        });
      }
    } catch (err) {
      errorRef.current += 1;
      setErrorCount(v => v + 1);
      addLog({
        type: "error",
        userId,
        msg: err.name === "TimeoutError" ? "Timeout" : "Rede",
        time: new Date().toLocaleTimeString("pt-BR"),
      });
    }
  }, [addLog]);

  // Motor principal - lança próxima requisição
  const launchNext = useCallback(() => {
    if (!isRunningRef.current || isPausedRef.current) return;
    if (activeCountRef.current >= concurrencyRef.current) return;

    const myId = currentIdRef.current;
    currentIdRef.current += 1;
    activeCountRef.current += 1;

    setCurrentId(myId);
    persistState();

    fetchUserId(myId).finally(() => {
      activeCountRef.current -= 1;
      // Se ainda rodando, lança próximo
      if (isRunningRef.current && !isPausedRef.current) {
        launchNextRef.current?.();
      }
    });
  }, [fetchUserId, persistState]);

  // Manter ref atualizada
  useEffect(() => {
    launchNextRef.current = launchNext;
  }, [launchNext]);

  // Auto-start ao montar se estava rodando antes do reload
  useEffect(() => {
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;

    if (initialStatus === "running") {
      // Estava rodando → reiniciar automaticamente
      isRunningRef.current = true;
      isPausedRef.current = false;
      // Pequeno delay para garantir que launchNextRef está populado
      setTimeout(() => {
        for (let i = 0; i < concurrencyRef.current; i++) {
          launchNextRef.current?.();
        }
      }, 300);
    } else if (initialStatus === "paused") {
      // Estava pausado → mantém pausado (não lança requests)
      isRunningRef.current = true;
      isPausedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iniciar bot
  const start = useCallback(() => {
    if (isRunningRef.current && !isPausedRef.current) return;

    if (isPausedRef.current) {
      // Retomar do pause
      isPausedRef.current = false;
      setStatus("running");
      persistState("running");
      // Preencher slots vazios
      const needed = concurrencyRef.current - activeCountRef.current;
      for (let i = 0; i < needed; i++) {
        launchNextRef.current?.();
      }
      return;
    }

    // Iniciar do zero / início
    isRunningRef.current = true;
    isPausedRef.current = false;
    setStatus("running");
    persistState("running");

    for (let i = 0; i < concurrencyRef.current; i++) {
      launchNextRef.current?.();
    }
  }, [persistState]);

  // Pausar bot
  const pause = useCallback(() => {
    isPausedRef.current = true;
    setStatus("paused");
    persistState("paused");
  }, [persistState]);

  // Parar bot
  const stop = useCallback(() => {
    isRunningRef.current = false;
    isPausedRef.current = false;
    activeCountRef.current = 0;
    setStatus("idle");
    persistState("idle");
  }, [persistState]);

  // Resetar contadores
  const reset = useCallback(() => {
    stop();
    successRef.current = 0;
    errorRef.current = 0;
    setSuccessCount(0);
    setErrorCount(0);
    setLogs([]);
    reqTimestampsRef.current = [];
    currentIdRef.current = startId;
    setCurrentId(startId);
    persistState();
  }, [stop, startId, persistState]);

  // Atualizar concorrência
  const updateConcurrency = useCallback((val) => {
    const n = Math.max(1, Math.min(20, val));
    concurrencyRef.current = n;
    setConcurrency(n);
    // Se rodando, lançar mais slots se necessário
    if (isRunningRef.current && !isPausedRef.current) {
      const needed = n - activeCountRef.current;
      for (let i = 0; i < needed; i++) {
        launchNextRef.current?.();
      }
    }
    persistState();
  }, [persistState]);

  // Atualizar ID inicial
  const updateStartId = useCallback((val) => {
    const n = Math.max(0, val);
    setStartId(n);
    if (status === "idle") {
      currentIdRef.current = n;
      setCurrentId(n);
    }
  }, [status]);

  // Ir para ID específico
  const goToId = useCallback((val) => {
    const n = Math.max(0, val);
    currentIdRef.current = n;
    setCurrentId(n);
    persistState();
  }, [persistState]);

  return {
    status,
    startId,
    currentId,
    concurrency,
    successCount,
    errorCount,
    logs,
    reqPerMin,
    start,
    pause,
    stop,
    reset,
    updateConcurrency,
    updateStartId,
    goToId,
  };
}
